/**
 * Live Web Audio synthesis for the seeded synth study. No samples: every note is
 * built from oscillators, filters and envelopes (subtractive pads/bass/leads,
 * FM bells and electric piano, synthesized drums). The same graph runs on an
 * AudioContext (live, lookahead scheduler, loops) or an OfflineAudioContext
 * (calibration and WAV export).
 */
import type { FxParams, InstId, Patch, SNote, SynthScore } from './compose';

const midi = (p: number) => 440 * 2 ** ((p - 69) / 12);
export const INSTRUMENT_LABELS: Record<InstId, string> = {
  pad: 'Pad', stab: 'Pulse chords', drone: 'Drone', bass: 'Bass', arp: 'Arp', lead: 'Lead', bell: 'Bell', keys: 'E-piano',
  kick: 'Kick', snare: 'Snare', clap: 'Clap', hat: 'Hi-hat', ohat: 'Open hat', shaker: 'Shaker', tom: 'Toms', riser: 'Riser', crash: 'Crash',
};
/** Mix targets (dB RMS of one calibrated note, relative). */
const TARGET_DB: Record<InstId, number> = {
  // Sustained parts measure louder per note than they feel under a melody, so
  // pads/drones sit well below the lead; short hits are measured on their transient.
  kick: -7, snare: -7, clap: -11, hat: -28, ohat: -26, shaker: -22, tom: -15, riser: -24, crash: -24,
  bass: -13, stab: -21, pad: -24.5, drone: -23, arp: -21, lead: -14.5, bell: -19, keys: -20,
};

function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  const b = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate), d = b.getChannelData(0);
  let s = 0x9e3779b9;
  for (let i = 0; i < d.length; i++) { s ^= s << 13; s ^= s >>> 17; s ^= s << 5; d[i] = (s >>> 0) / 2147483648 - 1; }
  return b;
}
/**
 * Freeverb-style algorithmic reverb (8 damped combs → 4 all-passes per side).
 * Deterministic and cheap, unlike a long ConvolverNode (whose tail runs on
 * background threads and can differ between renders — fatal for chunking).
 * Comb feedback is set from the requested RT60; `tone` damps the tail.
 */
export function algorithmicReverb(ctx: BaseAudioContext, seconds: number, tone: number, input: AudioNode, output: AudioNode): void {
  const sr = ctx.sampleRate, k = sr / 44100;
  const combs = [1116, 1188, 1277, 1356, 1422, 1491, 1557, 1617], allpasses = [556, 441, 341, 225];
  const merger = ctx.createChannelMerger(2);
  for (const [side, spread] of [[0, 0], [1, 23]] as const) {
    const sum = ctx.createGain(); sum.gain.value = .1 * 8 / combs.length; // calibrated: ~-10 dB room share at send .35
    const pre = ctx.createGain(); pre.gain.value = 1; input.connect(pre);
    for (const c of combs) {
      // One-pole low-pass damping (gain ≤ 1 at every frequency). A BiquadFilter
      // here overshoots unity and makes the comb self-oscillate.
      const a = Math.exp(-2 * Math.PI * Math.min(sr * .45, tone) / sr);
      const d = ctx.createDelay(1), fb = ctx.createGain(), damp = ctx.createIIRFilter([1 - a], [1, -a]);
      const delay = Math.max(128, Math.round((c + spread) * k)) / sr;
      d.delayTime.value = delay;
      fb.gain.value = Math.min(.97, 10 ** (-3 * delay / Math.max(.3, seconds))); // RT60
      pre.connect(d); d.connect(damp); damp.connect(fb); fb.connect(d); damp.connect(sum);
    }
    let node: AudioNode = sum;
    for (const a of allpasses) { // Schroeder all-pass: v = x + g·v[n-D]; y = -g·v + v[n-D]
      const g = .5, v = ctx.createGain(), d = ctx.createDelay(1), fb = ctx.createGain(), neg = ctx.createGain(), y = ctx.createGain();
      d.delayTime.value = Math.max(128, Math.round((a + spread) * k)) / sr; fb.gain.value = g; neg.gain.value = -g;
      node.connect(v); v.connect(d); d.connect(fb); fb.connect(v); v.connect(neg); neg.connect(y); d.connect(y);
      node = y;
    }
    node.connect(merger, 0, side);
  }
  merger.connect(output);
}
function driveCurve(amount: number): Float32Array {
  const n = 2048, c = new Float32Array(n), k = 1 + amount * 6;
  for (let i = 0; i < n; i++) { const x = i / (n - 1) * 2 - 1; c[i] = Math.tanh(k * x) / Math.tanh(k); }
  return c;
}
const pulseCache = new WeakMap<BaseAudioContext, Map<number, PeriodicWave>>();
function pulseWave(ctx: BaseAudioContext, duty = .25): PeriodicWave {
  let byDuty = pulseCache.get(ctx); if (!byDuty) pulseCache.set(ctx, byDuty = new Map());
  let w = byDuty.get(duty);
  if (!w) {
    const n = 64, re = new Float32Array(n), im = new Float32Array(n);
    for (let k = 1; k < n; k++) im[k] = 2 / (k * Math.PI) * Math.sin(Math.PI * k * duty) * Math.cos(Math.PI * k * duty), re[k] = 2 / (k * Math.PI) * Math.sin(Math.PI * k * duty) * Math.sin(Math.PI * k * duty);
    w = ctx.createPeriodicWave(re, im); byDuty.set(duty, w);
  }
  return w;
}

interface Channel { input: GainNode; level: GainNode; duck: GainNode; analyser?: AnalyserNode }

export class SynthEngine {
  readonly master: GainNode;
  readonly analyser: AnalyserNode;
  readonly channels = new Map<InstId, Channel>();
  private noise: AudioBuffer;
  private tone: BiquadFilterNode;
  private reverbIn: GainNode;
  private delayIn: GainNode;
  private chorusIn: GainNode;
  private nodes: AudioNode[] = [];
  /** `timeOrigin`: the song time (s) at context time 0, so free-running LFOs keep
   *  one phase across separately rendered chunks. */
  constructor(readonly ctx: BaseAudioContext, readonly score: SynthScore, dest: AudioNode, gains?: Partial<Record<InstId, number>>, meters = false, readonly timeOrigin = 0) {
    const fx = score.fx;
    this.noise = noiseBuffer(ctx);
    const pre = ctx.createGain(), shaper = ctx.createWaveShaper(), tone = ctx.createBiquadFilter(), comp = ctx.createDynamicsCompressor();
    this.master = ctx.createGain(); this.analyser = ctx.createAnalyser(); this.analyser.fftSize = 2048;
    this.analyser.smoothingTimeConstant = 0; // Visual meters own release; preserve note attacks.
    shaper.curve = driveCurve(fx.drive * .25) as Float32Array<ArrayBuffer>; shaper.oversample = '2x';
    this.tone = tone; tone.type = 'lowpass'; tone.frequency.value = Math.min(fx.tone, ctx.sampleRate * .45); tone.Q.value = .4;
    // Gentle glue compression. Attack ≥ 10 ms: faster tracks individual bass cycles and distorts them.
    comp.threshold.value = -20; comp.ratio.value = 2.2; comp.attack.value = .012; comp.release.value = .22; comp.knee.value = 8;
    // GAIN STAGING: calibrated channels sum well above full scale, and a WaveShaper
    // hard-clips anything beyond ±1. 12 dB of headroom keeps the saturation a gentle
    // warmth; make-up gain comes after the compressor; the limiter is the last stage.
    pre.gain.value = .25;
    this.master.gain.value = fx.master * 2.2;
    const limiter = ctx.createDynamicsCompressor();
    // Safety only: with proper headroom it should rarely act (a busy fast limiter distorts bass).
    limiter.threshold.value = -1; limiter.ratio.value = 20; limiter.attack.value = .005; limiter.release.value = .25; limiter.knee.value = 0;
    pre.connect(shaper); shaper.connect(tone); tone.connect(comp); comp.connect(this.master); this.master.connect(limiter); limiter.connect(this.analyser); this.analyser.connect(dest);
    // Reverb
    this.reverbIn = ctx.createGain();

    const revOut = ctx.createGain(); revOut.gain.value = .9;
    algorithmicReverb(ctx, fx.reverbSeconds, fx.reverbTone, this.reverbIn, revOut); revOut.connect(pre);
    // Ping-pong delay, tempo synced, darkening feedback.
    this.delayIn = ctx.createGain();
    const beat = 60 / score.bpm, dl = ctx.createDelay(4), dr = ctx.createDelay(4), fb = ctx.createGain(), damp = ctx.createBiquadFilter();
    const pl = ctx.createStereoPanner(), pr = ctx.createStereoPanner(), dOut = ctx.createGain();
    dl.delayTime.value = beat * fx.delayBeats; dr.delayTime.value = beat * fx.delayBeats;
    fb.gain.value = fx.delayFeedback; damp.type = 'lowpass'; damp.frequency.value = 3200; pl.pan.value = -.7 * (fx.width ?? 1); pr.pan.value = .7 * (fx.width ?? 1); dOut.gain.value = .6;
    this.delayIn.connect(dl); dl.connect(pl); dl.connect(dr); dr.connect(pr); dr.connect(damp); damp.connect(fb); fb.connect(dl);
    pl.connect(dOut); pr.connect(dOut); dOut.connect(pre); dOut.connect(this.reverbIn);
    // Stereo chorus for pads.
    this.chorusIn = ctx.createGain();
    for (const [side, rate] of [[-1, .31], [1, .43]] as const) {
      const d = ctx.createDelay(.05), lfo = ctx.createOscillator(), depth = ctx.createGain(), pan = ctx.createStereoPanner(), g = ctx.createGain();
      d.delayTime.value = .014; lfo.frequency.value = rate; depth.gain.value = .0035; pan.pan.value = side * .8 * (fx.width ?? 1); g.gain.value = .55;
      const phase = 2 * Math.PI * rate * timeOrigin; // sin(x + phase) as a one-harmonic wave
      lfo.setPeriodicWave(ctx.createPeriodicWave(new Float32Array([0, Math.sin(phase)]), new Float32Array([0, Math.cos(phase)]), { disableNormalization: true }));
      lfo.connect(depth); depth.connect(d.delayTime); lfo.start();
      this.chorusIn.connect(d); d.connect(pan); pan.connect(g); g.connect(pre); g.connect(this.reverbIn);
      this.nodes.push(lfo);
    }
    for (const id of Object.keys(score.patches) as InstId[]) {
      const patch = score.patches[id]!;
      const input = ctx.createGain(), level = ctx.createGain(), duck = ctx.createGain();
      level.gain.value = gains?.[id] ?? 1;
      input.connect(duck); duck.connect(level); level.connect(pre);
      const rs = ctx.createGain(); rs.gain.value = patch.send.reverb; level.connect(rs); rs.connect(this.reverbIn);
      if (patch.send.delay) { const ds = ctx.createGain(); ds.gain.value = patch.send.delay; level.connect(ds); ds.connect(this.delayIn); }
      if (patch.chorus) { const cs = ctx.createGain(); cs.gain.value = .7; level.connect(cs); cs.connect(this.chorusIn); }
      let analyser: AnalyserNode | undefined;
      if (meters) { analyser = ctx.createAnalyser(); analyser.fftSize = 512; level.connect(analyser); }
      this.channels.set(id, { input, level, duck, analyser });
    }
  }

  /** Schedule one note at absolute context time `t`. */
  play(n: SNote, t: number): void {
    const ch = this.channels.get(n.inst), patch = this.score.patches[n.inst];
    if (!ch || !patch) return;
    const dur = n.beats * 60 / this.score.bpm;
    let out: AudioNode = ch.input;
    if (n.pan) { const p = this.ctx.createStereoPanner(); p.pan.value = n.pan * (this.score.fx.width ?? 1); p.connect(ch.input); out = p; }
    if (n.throw) { // echo throw: the phrase ending also goes hard into the delay
      const g = this.ctx.createGain(), send = this.ctx.createGain(); send.gain.value = .9;
      g.connect(out); g.connect(send); send.connect(this.delayIn); out = g;
    }
    switch (n.inst) {
      case 'pad': case 'drone': this.pad(n, patch, t, dur, out); break;
      case 'bass': this.mono(n, patch, t, dur, out, true); break;
      case 'arp': case 'stab': this.mono(n, patch, t, dur, out, false); break;
      case 'lead': this.lead(n, patch, t, dur, out); break;
      case 'bell': this.fm(n, patch, t, dur, out, false); break;
      case 'keys': this.fm(n, patch, t, dur, out, true); break;
      case 'kick': this.kick(n, t, out); this.duck(t); break;
      case 'snare': case 'clap': this.snare(n, t, out, n.inst === 'clap'); break;
      case 'hat': case 'ohat': case 'shaker': case 'crash': this.hat(n, t, out); break;
      case 'tom': this.tom(n, t, out); break;
      case 'riser': this.riser(n, t, dur, out); break;
    }
  }

  private osc(wave: Patch['wave'], f: number, t: number, end: number, detune = 0, duty?: number): OscillatorNode {
    const o = this.ctx.createOscillator();
    f = Math.min(f, this.ctx.sampleRate * .45);
    if (wave === 'pulse') o.setPeriodicWave(pulseWave(this.ctx, duty)); else o.type = wave;
    o.frequency.value = f; o.detune.value = detune; o.start(t); o.stop(end);
    return o;
  }
  private amp(t: number, peak: number, a: number, d: number, s: number, dur: number, r: number): { gain: GainNode; end: number } {
    const g = this.ctx.createGain(), gg = g.gain, off = t + Math.max(dur, a + .005);
    gg.setValueAtTime(0, t); gg.linearRampToValueAtTime(peak, t + a);
    if (d > 0 && s < 1) gg.setTargetAtTime(peak * s, t + a, d / 3);
    gg.setTargetAtTime(0, off, r / 4); // release continues from wherever the decay reached
    return { gain: g, end: off + r * 1.5 };
  }
  private pad(n: SNote, p: Patch, t: number, dur: number, out: AudioNode): void {
    const f = midi(n.pitch), { gain, end } = this.amp(t, n.vel * .25, p.attack, .5, 1, dur, p.release);
    const filt = this.ctx.createBiquadFilter(); filt.type = 'lowpass'; filt.Q.value = p.resonance;
    const base = p.cutoff * (1 + (n.pitch - 60) / 48), cap = this.ctx.sampleRate * .4;
    const c = Math.min(cap, base * (n.bright ?? 1)), cEnd = Math.min(cap, base * (n.brightEnd ?? n.bright ?? 1));
    filt.frequency.setValueAtTime(c * (1 - p.envAmount * .6), t); filt.frequency.linearRampToValueAtTime(c, t + p.attack * 1.3);
    if (cEnd !== c) filt.frequency.linearRampToValueAtTime(cEnd, t + Math.max(dur, p.attack * 1.3 + .01)); // slow sweep across the held chord
    if (n.inst === 'pad' && p.lfo) { const l = this.ctx.createOscillator(), lg = this.ctx.createGain(); l.frequency.value = .11 + (n.pitch % 5) * .013; lg.gain.value = c * p.lfo * .5; l.connect(lg); lg.connect(filt.frequency); l.start(t); l.stop(end); }
    if (n.inst === 'drone') { const l = this.ctx.createOscillator(), lg = this.ctx.createGain(); l.frequency.value = .07; lg.gain.value = c * .5; l.connect(lg); lg.connect(filt.frequency); l.start(t); l.stop(end); }
    for (let v = 0; v < p.voices; v++) {
      const o = this.osc(p.wave, f, t, end, (v - (p.voices - 1) / 2) * p.detune);
      const pan = this.ctx.createStereoPanner(); pan.pan.value = p.voices > 1 ? (v / (p.voices - 1) * 2 - 1) * .5 * (this.score.fx.width ?? 1) : 0;
      o.connect(pan); pan.connect(filt);
    }
    filt.connect(gain); gain.connect(out);
  }
  private mono(n: SNote, p: Patch, t: number, dur: number, out: AudioNode, bass: boolean): void {
    const f = midi(n.pitch), { gain, end } = this.amp(t, n.vel * .5, .004, p.decay, p.sustain, dur, p.release);
    const filt = this.ctx.createBiquadFilter(); filt.type = 'lowpass'; filt.Q.value = p.resonance;
    const cut = p.cutoff * (n.bright ?? 1);
    filt.frequency.setValueAtTime(Math.min(this.ctx.sampleRate * .45, cut * (1 + p.envAmount)), t); filt.frequency.setTargetAtTime(cut, t, p.decay / 2.5);
    this.osc(p.wave, f, t, end, 0, p.duty).connect(filt);
    if (bass && p.sub) { const sg = this.ctx.createGain(); sg.gain.value = p.sub; this.osc('sine', f / 2 < 30 ? f : f / 2, t, end).connect(sg); sg.connect(gain); }
    let node: AudioNode = filt;
    if (bass && p.drive > .05) { const sh = this.ctx.createWaveShaper(); sh.curve = driveCurve(p.drive) as Float32Array<ArrayBuffer>; sh.oversample = '4x'; filt.connect(sh); node = sh; } // oversampled: no aliasing fizz
    node.connect(gain); gain.connect(out);
  }
  private lead(n: SNote, p: Patch, t: number, dur: number, out: AudioNode): void {
    const f = midi(n.pitch), { gain, end } = this.amp(t, n.vel * .28, p.attack, .3, p.sustain, dur, p.release);
    const filt = this.ctx.createBiquadFilter(); filt.type = 'lowpass'; filt.Q.value = p.resonance;
    const cut = Math.min(this.ctx.sampleRate * .45, p.cutoff * (n.bright ?? 1));
    filt.frequency.value = cut;
    // Optional filter envelope (v5 lead voices): a soft "wah" attack settling to the cutoff.
    if (p.envAmount > 0) { filt.frequency.setValueAtTime(Math.min(this.ctx.sampleRate * .45, cut * (1 + p.envAmount)), t); filt.frequency.setTargetAtTime(cut, t + p.attack, p.decay / 2.5); }
    const vib = this.ctx.createOscillator(), vg = this.ctx.createGain();
    vib.frequency.value = 5.2; vg.gain.setValueAtTime(0, t); vg.gain.linearRampToValueAtTime(f * p.vibrato, t + Math.min(.6, dur * .7) + .05);
    vib.connect(vg); vib.start(t); vib.stop(end);
    for (let v = 0; v < p.voices; v++) {
      const o = this.osc(p.wave, f, t, end, v ? p.detune : 0, p.duty);
      if (n.glide && this.prevLead) { o.frequency.setValueAtTime(this.prevLead, t); o.frequency.exponentialRampToValueAtTime(f, t + p.glide); }
      if (n.bend) { const from = t + Math.max(.05, dur * .45); o.frequency.setValueAtTime(f, from); o.frequency.exponentialRampToValueAtTime(f * 2 ** (n.bend / 12), t + dur + p.release * .5); }
      vg.connect(o.frequency); o.connect(filt);
    }
    this.prevLead = f;
    filt.connect(gain); gain.connect(out);
  }
  private prevLead = 0;
  private fm(n: SNote, p: Patch, t: number, dur: number, out: AudioNode, keys: boolean): void {
    const f = midi(n.pitch), ring = keys ? Math.max(dur, .2) : p.decay;
    const g = this.ctx.createGain(), end = t + (keys ? ring + p.release * 1.5 : p.decay * 1.3);
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(n.vel * (keys ? .3 : .22), t + (keys ? .003 : .012)); // bells: softer strike
    g.gain.setTargetAtTime(0, t + .003, keys ? p.decay / 2 : p.decay / 4);
    if (keys) { g.gain.cancelScheduledValues(t + ring); g.gain.setTargetAtTime(0, t + ring, p.release / 3); }
    // Keep the modulator (and its sidebands) below Nyquist: high notes lower the ratio.
    const nyq = this.ctx.sampleRate * .45, ratio = f * p.ratio > nyq ? Math.max(1, Math.floor(nyq / f)) : p.ratio;
    const car = this.osc('sine', f, t, end), mod = this.osc('sine', f * ratio, t, end), mg = this.ctx.createGain();
    // Bells lose their brightness quickly, leaving a round, sine-like ring.
    mg.gain.setValueAtTime(f * ratio * p.index, t); mg.gain.setTargetAtTime(f * ratio * p.index * (keys ? .15 : .05), t, keys ? .25 : Math.min(.18, p.decay / 8));
    mod.connect(mg); mg.connect(car.frequency);
    if (keys) car.connect(g);
    else { const soft = this.ctx.createBiquadFilter(); soft.type = 'lowpass'; soft.frequency.value = Math.min(this.ctx.sampleRate * .45, Math.max(1800, f * 3)); soft.Q.value = .5; car.connect(soft); soft.connect(g); }
    if (keys && f * 7.1 < nyq) { // tine: a short bright partial
      const tine = this.osc('sine', f * 7.1, t, t + .4), tg = this.ctx.createGain();
      tg.gain.setValueAtTime(n.vel * .03, t); tg.gain.setTargetAtTime(0, t, .05); tine.connect(tg); tg.connect(g);
    }
    g.connect(out);
  }
  private kick(n: SNote, t: number, out: AudioNode): void {
    const o = this.osc('sine', 150, t, t + .6), g = this.ctx.createGain();
    o.frequency.setValueAtTime(155, t); o.frequency.exponentialRampToValueAtTime(46, t + .11);
    g.gain.setValueAtTime(n.vel, t); g.gain.setTargetAtTime(0, t + .02, .12);
    o.connect(g); g.connect(out);
    const c = this.ctx.createBufferSource(), cf = this.ctx.createBiquadFilter(), cg = this.ctx.createGain();
    c.buffer = this.noise; cf.type = 'highpass'; cf.frequency.value = 2500; cg.gain.setValueAtTime(n.vel * .25, t); cg.gain.setTargetAtTime(0, t, .004);
    c.connect(cf); cf.connect(cg); cg.connect(out); c.start(t); c.stop(t + .05);
  }
  private snare(n: SNote, t: number, out: AudioNode, clap: boolean): void {
    const src = this.ctx.createBufferSource(), bp = this.ctx.createBiquadFilter(), g = this.ctx.createGain();
    src.buffer = this.noise; bp.type = 'bandpass'; bp.frequency.value = clap ? 1300 : 1900; bp.Q.value = clap ? 1.2 : .7;
    g.gain.setValueAtTime(0, t);
    if (clap) for (const k of [0, .011, .023]) { g.gain.setValueAtTime(n.vel * .8, t + k); g.gain.setTargetAtTime(n.vel * .15, t + k, .003); }
    else g.gain.setValueAtTime(n.vel * .7, t);
    g.gain.setTargetAtTime(0, t + (clap ? .03 : 0), clap ? .06 : .05);
    src.connect(bp); bp.connect(g); g.connect(out); src.start(t, this.noiseOffset(t, 7.3, 1.5)); src.stop(t + .4);
    if (!clap) { const o = this.osc('triangle', 190, t, t + .2), og = this.ctx.createGain(); og.gain.setValueAtTime(n.vel * .5, t); og.gain.setTargetAtTime(0, t, .03); o.frequency.exponentialRampToValueAtTime(150, t + .08); o.connect(og); og.connect(out); }
  }
  private hat(n: SNote, t: number, out: AudioNode): void {
    const src = this.ctx.createBufferSource(), hp = this.ctx.createBiquadFilter(), g = this.ctx.createGain();
    const open = n.inst === 'ohat', shaker = n.inst === 'shaker', crash = n.inst === 'crash';
    src.buffer = this.noise; hp.type = shaker ? 'bandpass' : 'highpass'; hp.frequency.value = Math.min(this.ctx.sampleRate * .4, shaker ? 5200 : crash ? 3800 : 7500); hp.Q.value = shaker ? 1.5 : .7;
    g.gain.setValueAtTime(n.vel * .5, t); g.gain.setTargetAtTime(0, t + .002, crash ? .55 : open ? .09 : shaker ? .025 : .012);
    src.connect(hp); hp.connect(g); g.connect(out); src.start(t, this.noiseOffset(t, 3.1, .5)); src.stop(t + (crash ? 1.9 : open ? .6 : .15));
  }
  private tom(n: SNote, t: number, out: AudioNode): void {
    const f = midi(n.pitch), o = this.osc('sine', f, t, t + .5), g = this.ctx.createGain();
    o.frequency.setValueAtTime(f * 1.6, t); o.frequency.exponentialRampToValueAtTime(f, t + .06);
    g.gain.setValueAtTime(n.vel * .8, t); g.gain.setTargetAtTime(0, t + .01, .1); o.connect(g); g.connect(out);
  }
  private riser(n: SNote, t: number, dur: number, out: AudioNode): void {
    const src = this.ctx.createBufferSource(), bp = this.ctx.createBiquadFilter(), g = this.ctx.createGain();
    src.buffer = this.noise; src.loop = true; bp.type = 'bandpass'; bp.Q.value = 2.5;
    bp.frequency.setValueAtTime(250, t); bp.frequency.exponentialRampToValueAtTime(Math.min(7000, this.ctx.sampleRate * .4), t + dur);
    g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(n.vel * .6, t + dur * .95); g.gain.linearRampToValueAtTime(0, t + dur + .05);
    src.connect(bp); bp.connect(g); g.connect(out); src.start(t); src.stop(t + dur + .1);
  }
  /** Noise read position from absolute song time (identical across chunk renders). */
  private noiseOffset(t: number, rate: number, span: number): number { const x = ((t + this.timeOrigin) * rate) % span; return x < 0 ? x + span : x; }
  /** Sidechain: every kick briefly ducks the pads/arps. */
  private duck(t: number): void {
    const depth = this.score.fx.duckDepth; if (!depth) return;
    for (const [id, ch] of this.channels) {
      if (!this.score.patches[id]?.duck) continue;
      const g = ch.duck.gain; g.cancelScheduledValues(t); g.setValueAtTime(1 - depth, t); g.setTargetAtTime(1, t + .02, .09);
    }
  }
  /** Master tone automation point (ramps from the previous point). */
  automate(tone: number, t: number): void { this.tone.frequency.linearRampToValueAtTime(Math.min(tone, this.ctx.sampleRate * .45), t); }
  setLevel(id: InstId, value: number): void { const ch = this.channels.get(id); if (ch && this.ctx instanceof AudioContext) ch.level.gain.setTargetAtTime(value, this.ctx.currentTime, .02); }
  dispose(): void { for (const n of this.nodes) try { (n as OscillatorNode).stop(); } catch { /* already stopped */ } this.master.disconnect(); this.analyser.disconnect(); }
}

/** Render a representative note per instrument offline; return gains that hit the mix targets. */
export async function calibrate(score: SynthScore): Promise<Partial<Record<InstId, number>>> {
  const out: Partial<Record<InstId, number>> = {}, sr = 22050;
  for (const id of Object.keys(score.patches) as InstId[]) {
    const sample = score.notes.find(n => n.inst === id);
    if (!sample) continue; // Excerpt exports may retain patches with no notes.
    const seconds = Math.min(4, Math.max(1, sample.beats * 60 / score.bpm + 1.5));
    const ctx = new OfflineAudioContext(1, Math.ceil(sr * seconds), sr);
    const dry: SynthScore = { ...score, fx: { ...score.fx, drive: 0, duckDepth: 0 }, patches: { [id]: { ...score.patches[id]!, send: { reverb: 0, delay: 0 }, chorus: false } } };
    // Measure the instrument's own channel, before the master bus (headroom, glue
    // compression and make-up gain are the mix's business, not the calibration's).
    const sink = ctx.createGain(), e = new SynthEngine(ctx, dry, sink, undefined);
    e.channels.get(id)!.level.connect(ctx.destination);
    e.play({ ...sample, beat: 0, beats: Math.min(sample.beats, 8) }, .01);
    const buf = await ctx.startRendering(), d = buf.getChannelData(0);
    // RMS over the loudest 300 ms window: a note's body, not its tail.
    // Short hits are measured over their own length: a 12 ms hat averaged over 300 ms
    // read ~14 dB too quiet and was boosted into full-scale spikes.
    const shortHit = ['hat', 'ohat', 'shaker', 'clap', 'snare', 'crash', 'kick', 'tom'].includes(id);
    const w = Math.floor(sr * (shortHit ? (id === 'hat' || id === 'shaker' ? .02 : .05) : .3)); let best = 0, acc = 0;
    for (let i = 0; i < d.length; i++) { acc += d[i] * d[i]; if (i >= w) acc -= d[i - w] * d[i - w]; if (i >= w - 1) best = Math.max(best, acc / w); }
    const rms = Math.sqrt(best) || 1e-4;
    out[id] = Math.min(8, 10 ** ((TARGET_DB[id] + (score.mix?.[id] ?? 0)) / 20) / rms);
  }
  return out;
}

/** Loop-aware lookahead scheduler for live playback. */
export class SynthPlayer {
  engine: SynthEngine | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private origin = 0; private cursor = 0; private loops = 0;
  private out: GainNode;
  readonly levels = new Map<InstId, number>();
  muted = new Set<InstId>(); solo = new Set<InstId>();
  private gains: Partial<Record<InstId, number>> = {};
  private prepareAbort = new AbortController();
  private transportBeat:(()=>number)|null=null;
  /** `lookahead` (s): how far ahead notes are scheduled. The lab uses a short one for
   *  instant mute/solo; the game a long one so frame hitches can never make notes late. */
  constructor(readonly ctx: AudioContext, readonly score: SynthScore, dest: AudioNode = ctx.destination, private lookahead = .25) { this.out = ctx.createGain(); this.out.connect(dest); }
  get secondsPerBeat(): number { return 60 / this.score.bpm; }
  /** Position in beats within the loop (for the UI playhead). */
  get beat(): number { return this.engine ? ((this.ctx.currentTime - this.origin) / this.secondsPerBeat) % this.score.beats : 0; }
  get playing(): boolean { return this.timer !== null; }
  setVolume(v: number): void { this.out.gain.setTargetAtTime(v, this.ctx.currentTime, .03); }
  /** Calibrate levels (offline) ahead of start(); start() does it if needed. */
  async prepare(): Promise<void> {
    if (Object.keys(this.gains).length) return;
    this.prepareAbort.signal.throwIfAborted();
    this.gains = await calibrate(this.score);
  }
  async start(fromBeat: number | (()=>number) = 0, fadeIn = 0): Promise<void> {
    this.stop();
    await this.prepare();
    if (fadeIn > 0) { const g = this.out.gain, v = g.value, now = this.ctx.currentTime; g.cancelScheduledValues(now); g.setValueAtTime(0, now); g.linearRampToValueAtTime(v || 1, now + fadeIn); }
    this.prepareAbort.signal.throwIfAborted();
    this.engine = new SynthEngine(this.ctx, this.score, this.out, this.gains, true);
    this.applyMix();
    // Building the graph can take a noticeable fraction of a beat. Sample a
    // gameplay transport only after that work, at the actual scheduling origin.
    this.transportBeat=typeof fromBeat==='function'?fromBeat:null;
    const beat=typeof fromBeat==='function'?fromBeat():fromBeat;
    this.origin = this.ctx.currentTime + (this.transportBeat?0:.1) - beat * this.secondsPerBeat;
    this.cursor = beat + (this.transportBeat ? .1 / this.secondsPerBeat : 0); this.loops = 0;
    this.timer = setInterval(() => this.pump(), 25); this.pump();
  }
  private pump(): void {
    if (!this.engine) return;
    // Device audio clocks can drift from the round clock, particularly during
    // heavy rendering. Keep future notes on the same grid; already-sounding
    // voices continue naturally and the standalone music lab is unchanged.
    if(this.transportBeat)this.origin=this.ctx.currentTime-this.transportBeat()*this.secondsPerBeat;
    const horizon = (this.ctx.currentTime + this.lookahead - this.origin) / this.secondsPerBeat, total = this.score.beats;
    while (this.cursor < horizon) {
      const loopBase = this.loops * total, local = this.cursor - loopBase;
      if (local >= total) { this.loops++; continue; }
      const upto = Math.min(horizon - loopBase, total);
      for (const n of this.score.notes) if (n.beat >= local && n.beat < upto) this.engine.play(n, this.origin + (loopBase + n.beat) * this.secondsPerBeat);
      for (const a of this.score.automation ?? []) if (a.beat >= local && a.beat < upto) this.engine.automate(a.tone, this.origin + (loopBase + a.beat) * this.secondsPerBeat);
      this.cursor = loopBase + upto;
    }
  }
  applyMix(): void {
    if (!this.engine) return;
    for (const id of this.engine.channels.keys()) {
      const audible = this.solo.size ? this.solo.has(id) : !this.muted.has(id);
      this.engine.setLevel(id, audible ? (this.gains[id] ?? 1) : 0);
    }
  }
  meter(id: InstId): number {
    const a = this.engine?.channels.get(id)?.analyser; if (!a) return 0;
    const d = new Float32Array(a.fftSize); a.getFloatTimeDomainData(d);
    let s = 0; for (const x of d) s += x * x; return Math.sqrt(s / d.length);
  }
  /** Stop scheduling; already-sounding notes fade over `fade` seconds. */
  stop(fade = .08): void {
    this.transportBeat=null;
    if (this.timer) clearInterval(this.timer); this.timer = null;
    if (this.engine) {
      const e = this.engine; e.master.gain.cancelScheduledValues(this.ctx.currentTime);
      e.master.gain.setTargetAtTime(0, this.ctx.currentTime, Math.max(.02, fade / 4));
      setTimeout(() => { e.dispose(); }, (fade + .6) * 1000);
    }
    this.engine = null;
  }
  /** Loop position in beats — lets a replacement start on the same beat grid. */
  get position(): number { return this.beat; }
  dispose(): void { this.prepareAbort.abort(); this.stop(.05); setTimeout(() => this.out.disconnect(), 800); }
}

/** Render one pass of the loop (plus reverb tail) to a stereo WAV Blob. */
export async function renderWav(score: SynthScore, extraLoops = 0): Promise<Blob> {
  const sr = 44100, tail = 3, length = score.duration * (1 + extraLoops) + tail;
  const ctx = new OfflineAudioContext(2, Math.ceil(sr * length), sr);
  const gains = await calibrate(score);
  const e = new SynthEngine(ctx, score, ctx.destination, gains);
  for (let k = 0; k <= extraLoops; k++) {
    for (const n of score.notes) e.play(n, .05 + (k * score.beats + n.beat) * 60 / score.bpm);
    for (const a of score.automation ?? []) e.automate(a.tone, .05 + (k * score.beats + a.beat) * 60 / score.bpm);
  }
  const buf = await ctx.startRendering();
  const n = buf.length, data = new DataView(new ArrayBuffer(44 + n * 4));
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) data.setUint8(o + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); data.setUint32(4, 36 + n * 4, true); str(8, 'WAVE'); str(12, 'fmt '); data.setUint32(16, 16, true); data.setUint16(20, 1, true); data.setUint16(22, 2, true);
  data.setUint32(24, sr, true); data.setUint32(28, sr * 4, true); data.setUint16(32, 4, true); data.setUint16(34, 16, true); str(36, 'data'); data.setUint32(40, n * 4, true);
  const l = buf.getChannelData(0), r = buf.getChannelData(1);
  for (let i = 0; i < n; i++) { data.setInt16(44 + i * 4, Math.max(-1, Math.min(1, l[i])) * 32767, true); data.setInt16(46 + i * 4, Math.max(-1, Math.min(1, r[i])) * 32767, true); }
  return new Blob([data.buffer], { type: 'audio/wav' });
}
export type { FxParams };
